import { Mic } from 'lucide-react';
import React, { useState } from 'react';
import { cn } from '@/lib';

export function PTTIcon({
  isDisabled = false,
  isActive = false,
  onActivate,
  onDeactivate,
  onClick,
  size = 'sm',
  className,
  ...props
}) {
  // --- PRESERVE ORIGINAL CLICK BEHAVIOR ---
  const handleClick = e => {
    if (isDisabled) return;

    if (onClick) {
      onClick(e);
    } else if (isActive) {
      onDeactivate?.();
    } else {
      onActivate?.();
    }
  };

  // --- MINIMAL PRESS ONLY FOR SMALL SIZE ---
  const [isPressing, setIsPressing] = useState(false);

  const handlePressStart = () => {
    if (isDisabled) return;
    setIsPressing(true);
  };

  const handlePressEnd = () => {
    if (isDisabled) return;
    setIsPressing(false);
  };

  // --- SIZE CONFIGURATION ---
  const sizeConfig = {
    sm: {
      button: 'w-8 h-8',
      icon: 'w-4 h-4',
      rings: [{ size: 'w-[40px] h-[40px]' }, { size: 'w-[48px] h-[48px]' }, { size: 'w-[56px] h-[56px]' }],
    },
    lg: {
      button: 'w-16 h-16',
      icon: 'w-5 h-5',
      rings: [{ size: 'w-[68px] h-[68px]' }, { size: 'w-[74px] h-[74px]' }, { size: 'w-[80px] h-[80px]' }],
    },
  };

  const config = sizeConfig[size];

  // --- CONDITIONAL PRESS EVENTS (SMALL ONLY) ---
  const pressHandlers =
    size === 'sm'
      ? {
          onMouseDown: handlePressStart,
          onMouseUp: handlePressEnd,
          onMouseLeave: handlePressEnd,
          onTouchStart: handlePressStart,
          onTouchEnd: handlePressEnd,
        }
      : {};

  return (
    <button
      {...props}
      {...pressHandlers} // Only applied for small version
      data-testid="ptt-icon"
      disabled={isDisabled}
      onClick={handleClick}
      className={cn(
        'relative flex items-center justify-center rounded-full transition-all duration-150',

        // Only small button gets press animation
        size === 'sm' && isPressing && !isDisabled ? 'scale-[0.97]' : 'scale-100',

        config.button,
        !isDisabled && !isActive && 'bg border',
        isActive && 'bg-primary border-none',
        isDisabled && 'bg-disabled cursor-not-allowed',

        size === 'lg' && !isActive && 'hover:bg-elevated shadow-md hover:shadow-lg',
        size === 'lg' && isActive && 'shadow-md',
        size === 'sm' && 'shadow-sm',

        className
      )}
      aria-pressed={isActive}
    >
      {/* --- RIPPLES WHEN ACTIVE --- */}
      {isActive &&
        config.rings.map((ring, index) => (
          <div
            key={index}
            className={cn(`absolute rounded-full ptt-ring ${ring.size} pointer-events-none`)}
            style={{
              animationDelay: `${index * 0.25}s`,
            }}
          />
        ))}

      {/* --- INNER CIRCLE (SMALL ONLY) --- */}
      {size === 'sm' && (
        <div
          className={cn(
            'relative w-8 h-8 rounded-full shadow-sm transition-all duration-200 z-10',
            !isDisabled ? (isActive ? 'bg-primary border-none' : 'bg') : 'bg-muted',
            isActive && 'ptt-breath'
          )}
        />
      )}

      {/* --- MIC ICON --- */}
      <Mic
        className={cn(
          'absolute transition-colors duration-200 z-20',
          config.icon,
          size === 'lg' && 'relative z-20',
          isActive ? 'text-[var(--color-icon-onColor)]' : 'text-[var(--color-icon-primary)]',
          isDisabled && 'text-[var(--color-icon-disabled)]'
        )}
        strokeWidth={1.5}
      />
    </button>
  );
}
